export const STORE_IMAGE_ROLES = [
  { role: 'logoImage', label: 'Main logo' },
  { role: 'embeddedLogoImage', label: 'Embedded logo' },
  { role: 'SpecialLogoImage', label: 'Specials logo' },
  { role: 'listImage', label: 'List image' },
  { role: 'bannerImage', label: 'Banner image' },
] as const;

export type StoreImageRole = typeof STORE_IMAGE_ROLES[number]['role'];

export type ImageSource =
  | { kind: 'library'; assetId: string; src: string }
  | { kind: 'store-image'; role: StoreImageRole }
  | { kind: 'url'; src: string };

export type CmsStoreImages = {
  id: string;
  name: string;
  images: Record<StoreImageRole, string>;
};

type DispensaryWithImages = Partial<Record<StoreImageRole, unknown>> & {
  cName?: unknown;
  id?: unknown;
  name?: unknown;
};

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Builds exact-store metadata only when the resolved dispensary matches the requested id/cName. */
export function buildCmsStoreImages(
  dispensary: DispensaryWithImages | null | undefined,
  requestedStore: unknown
): CmsStoreImages | undefined {
  if (!dispensary || typeof dispensary.id !== 'string' || dispensary.id === '') return undefined;
  if (typeof requestedStore !== 'string' || requestedStore === '') return undefined;
  if (dispensary.id !== requestedStore && dispensary.cName !== requestedStore) return undefined;

  return {
    id: dispensary.id,
    name: stringValue(dispensary.name),
    images: Object.fromEntries(STORE_IMAGE_ROLES.map(({ role }) => [role, stringValue(dispensary[role])])) as Record<
      StoreImageRole,
      string
    >,
  };
}

export function isStoreImageRole(value: unknown): value is StoreImageRole {
  return STORE_IMAGE_ROLES.some(({ role }) => role === value);
}

export function resolveImageSource(
  value: unknown,
  storeImages: CmsStoreImages | undefined
): { source?: ImageSource; src: string; missingStoreRole: boolean } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { src: '', missingStoreRole: false };
  }

  const source = value as Partial<ImageSource>;
  if (source.kind === 'store-image' && isStoreImageRole(source.role)) {
    const normalized: ImageSource = { kind: 'store-image', role: source.role };
    return {
      source: normalized,
      src: storeImages?.images[source.role] ?? '',
      missingStoreRole: !storeImages?.images[source.role],
    };
  }

  if (source.kind === 'library' && typeof source.assetId === 'string' && typeof source.src === 'string') {
    return {
      source: { kind: 'library', assetId: source.assetId, src: source.src },
      src: source.src,
      missingStoreRole: false,
    };
  }

  if (source.kind === 'url' && typeof source.src === 'string') {
    return { source: { kind: 'url', src: source.src }, src: source.src, missingStoreRole: false };
  }

  return { src: '', missingStoreRole: false };
}

export function getCmsStoreImages(puck?: { metadata?: Record<string, unknown> }): CmsStoreImages | undefined {
  const candidate = puck?.metadata?.storeImages;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return undefined;

  const { id, name, images } = candidate as Partial<CmsStoreImages>;
  if (typeof id !== 'string' || typeof name !== 'string' || !images || typeof images !== 'object') return undefined;

  return {
    id,
    name,
    images: Object.fromEntries(
      STORE_IMAGE_ROLES.map(({ role }) => [role, stringValue((images as Record<string, unknown>)[role])])
    ) as Record<StoreImageRole, string>,
  };
}

export function storeImageRoleLabel(role: StoreImageRole): string {
  return STORE_IMAGE_ROLES.find((candidate) => candidate.role === role)?.label ?? role;
}
